import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import { useAccessLog } from "@/pages/AdminAccessLog";
import {
  EnvelopeSimple, Plug, CheckCircle, XCircle, Warning,
  ArrowClockwise, Key, Lock, Circle, PaperPlaneTilt,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminInboxSetup — one-page wizard to wire the user's Gmail into
 * Field Radar via IMAP polling. Steps:
 *  1. Generate a Google App Password (external link + copy help)
 *  2. Save the app password + email address
 *  3. Test the connection
 *  4. Flip on live sync — cron polls every 60s
 * Route: /admin/inbox-setup
 */
export default function AdminInboxSetup() {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [polling, setPolling] = useState(false);
  const [recent, setRecent] = useState([]);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${BASE}/api/inbox-imap/config`, auth()),
        axios.get(`${BASE}/api/inbox-imap/recent`, auth()),
      ]);
      setCfg(c.data);
      if (c.data.email && !email) setEmail(c.data.email);
      setRecent(r.data.rows || []);
    } catch {
      toast.error("Couldn't load inbox config");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const nav = useNavigate();
  useAccessLog("inbox_setup");
  const voidRow = async (row) => {
    if (!window.confirm(`Not a work order?\n\n"${row.subject}"\n\nThis cancels the WO it created.`)) return;
    try {
      const r = await axios.post(`${BASE}/api/inbox-imap/void`, { ids: [row.id] }, auth());
      toast.success(r.data.canceled_wos ? "WO canceled · email voided" : "Email voided");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Void failed")); }
  };

  const save = async ({ enabled = null } = {}) => {
    if (!email) { toast.error("Enter your Gmail address"); return; }
    if (!pw && !cfg?.has_password) { toast.error("Paste your Google App Password"); return; }
    setSaving(true);
    try {
      const payload = {
        email,
        app_password: pw || "placeholder-unchanged-000",  // sentinel; only used if new
        host: "imap.gmail.com", port: 993, mailbox: "INBOX",
        only_appfolio: true,
        enabled: enabled ?? (cfg?.enabled ?? true),
      };
      if (!pw && cfg?.has_password) {
        // Preserve existing password by fetching + re-saving without changing pw
        toast.info("Password unchanged");
        // We need to send a valid pw or the endpoint 422s — so just re-enable via disable endpoint OR require pw
        // Simpler: keep the current handling — force the user to re-enter password on any save
        if (enabled === false) {
          await axios.post(`${BASE}/api/inbox-imap/config/disable`, {}, auth());
          toast.success("Live sync disabled");
          load();
          setSaving(false);
          return;
        }
        setSaving(false);
        toast.error("Please re-paste your app password to save");
        return;
      }
      await axios.post(`${BASE}/api/inbox-imap/config`, payload, auth());
      toast.success("Saved");
      setPw("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${BASE}/api/inbox-imap/test`, {}, auth());
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Connected! Mailbox has ${r.data.mailbox_size} emails`);
      else toast.error(r.data.error || "Test failed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Test failed"));
    } finally { setTesting(false); }
  };

  const pollNow = async (days = 0) => {
    setPolling(true);
    try {
      const r = await axios.post(`${BASE}/api/inbox-imap/poll-now${days ? `?days=${days}` : ""}`, {}, auth());
      toast.success(`Scanned ${r.data.scanned}, ingested ${r.data.ingested}${days ? ` (last ${days} days)` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Poll failed"));
    } finally { setPolling(false); }
  };

  const disable = async () => {
    if (!window.confirm("Turn off live email sync? WOs will stop flowing until you re-enable.")) return;
    try {
      await axios.post(`${BASE}/api/inbox-imap/config/disable`, {}, auth());
      toast.success("Live sync disabled");
      load();
    } catch (e) { toast.error("Failed to disable"); }
  };

  const configured = !!cfg?.has_password;
  const live = configured && cfg?.enabled;

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-3xl mx-auto space-y-5" data-testid="inbox-setup-page">
        {/* Header */}
        <div className="flex items-start gap-3">
          <EnvelopeSimple weight="fill" size={30} className="text-cyan-600"/>
          <div className="flex-1">
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Live Email Sync
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-1">
              Poll your Gmail every 60 seconds — new AppFolio WOs, status changes, vendor assignments, and tenant replies flow straight in.
            </div>
          </div>
          <StatusPill live={live} configured={configured}/>
        </div>

        {/* Step 1 — App Password */}
        <StepCard n={1} title="Generate a Google App Password" done={configured}>
          <div className="text-sm text-slate-700 space-y-2">
            <div>1. Open <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-cyan-700 hover:underline font-bold">Google Security</a> and turn on <b>2-Step Verification</b> if it's not already on.</div>
            <div>2. Then open <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-cyan-700 hover:underline font-bold">Google App Passwords</a>.</div>
            <div>3. App name: <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">Field Radar</code> → click <b>Create</b>.</div>
            <div>4. Google shows a <b>16-character password</b> (with spaces). Copy the whole thing.</div>
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5">
              <Lock weight="fill" size={12} className="flex-none mt-0.5 text-amber-700"/>
              We encrypt your app password with Fernet before it hits the database. It's never logged, never visible in this UI after you save.
            </div>
          </div>
        </StepCard>

        {/* Step 2 — Save credentials */}
        <StepCard n={2} title="Paste credentials" done={configured}>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Gmail address</label>
              <input value={email} onChange={e => setEmail(e.target.value)}
                data-testid="imap-email-input"
                placeholder="andrew@gopmr.com"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1">
                <Key weight="fill" size={12}/> App password
                {configured && !pw && <span className="ml-1 text-emerald-700">· already saved (paste to update)</span>}
              </label>
              <input type="password" value={pw} onChange={e => setPw(e.target.value)}
                data-testid="imap-password-input"
                placeholder={configured ? "•••• •••• •••• ••••  (leave blank to keep current)" : "abcd efgh ijkl mnop"}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm font-mono"/>
            </div>
            <button onClick={save} disabled={saving}
              data-testid="imap-save-btn"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
              {saving ? "Saving…" : configured ? "Update credentials" : "Save credentials"}
            </button>
          </div>
        </StepCard>

        {/* Step 3 — Test connection */}
        <StepCard n={3} title="Test the connection" done={testResult?.ok}>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              We'll log into your Gmail read-only and report how many messages the mailbox has. Nothing is fetched or marked read yet.
            </div>
            <button onClick={test} disabled={testing || !configured}
              data-testid="imap-test-btn"
              className="h-10 px-4 border-2 border-cyan-500 hover:bg-cyan-50 text-cyan-700 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40">
              <Plug weight="fill" size={14}/> {testing ? "Connecting…" : "Test connection"}
            </button>
            {testResult && (
              <div className={`p-3 text-sm border-2 ${testResult.ok ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-rose-400 bg-rose-50 text-rose-800"} inline-flex items-start gap-2`}
                data-testid="imap-test-result">
                {testResult.ok ? <CheckCircle weight="fill" size={16}/> : <XCircle weight="fill" size={16}/>}
                <div>
                  {testResult.ok
                    ? <>Connected! Your inbox has <b>{testResult.mailbox_size}</b> messages.</>
                    : <>{testResult.error}</>}
                </div>
              </div>
            )}
          </div>
        </StepCard>

        {/* Step 4 — Go live */}
        <StepCard n={4} title="Turn on live sync" done={live}>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              Our cron polls every <b>60 seconds</b> and looks at the last 14 days — <b>read or unread</b> (reading mail on your phone no longer hides it). AppFolio-looking emails (from-address contains "appfolio", subject mentions Work Order / Vendor / ETA) and trusted PM senders get processed; nothing is marked read, and each Message-ID imports only once.
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => pollNow()} disabled={polling || !live}
                data-testid="imap-poll-now-btn"
                className="h-10 px-4 border-2 border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40">
                <ArrowClockwise weight="bold" size={14}/> {polling ? "Polling…" : "Poll right now"}
              </button>
              <button onClick={() => pollNow(30)} disabled={polling || !live}
                data-testid="imap-backfill-btn" title="Re-scan the last 30 days including read mail; already-imported WOs are skipped"
                className="h-10 px-4 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40">
                Backfill 30 days
              </button>
              {live ? (
                <button onClick={disable}
                  data-testid="imap-disable-btn"
                  className="h-10 px-4 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-xs tracking-widest">
                  Turn off
                </button>
              ) : configured && (
                <button onClick={() => save({ enabled: true })} disabled={saving}
                  data-testid="imap-enable-btn"
                  className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest">
                  Go live
                </button>
              )}
            </div>
            {cfg?.last_poll_at && (
              <div className="text-[11px] text-slate-500 space-y-0.5">
                <div>Last poll: <b className="text-slate-700">{new Date(cfg.last_poll_at).toLocaleString()}</b></div>
                <div>Total ingested since setup: <b className="text-slate-700">{cfg.total_ingested || 0}</b></div>
                {cfg.last_error && <div className="text-rose-600">Last error: {cfg.last_error}</div>}
              </div>
            )}
          </div>
        </StepCard>

        {/* Recent activity */}
        <div className="p-4 border-2 border-slate-200 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <PaperPlaneTilt weight="fill" size={14} className="text-cyan-600"/>
            <div className="font-black uppercase text-[11px] tracking-widest">
              Recent inbound ({recent.length})
            </div>
            <button onClick={load} className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
              Refresh
            </button>
          </div>
          {recent.length === 0 && (
            <div className="text-xs text-slate-400 italic p-4 text-center border-2 border-dashed border-slate-200">
              Nothing yet. Once live sync is on, ingested emails show here.
            </div>
          )}
          {recent.length > 0 && (
            <div className="divide-y divide-slate-100 border-2 border-slate-100 bg-slate-50/40">
              {recent.map((r, i) => (
                <div key={i} onClick={() => r.job_id && nav(`/admin/jobs/${r.job_id}`)}
                  className={`p-2 text-xs flex items-start gap-2 ${r.job_id ? "cursor-pointer hover:bg-cyan-50" : ""}`} data-testid={`recent-row-${i}`}>
                  <CheckCircle weight="fill" size={12} className={
                    r.routed_to === "prospects" ? "text-fuchsia-600 mt-0.5" :
                    r.routed_to === "existing_wo_update" ? "text-cyan-600 mt-0.5" :
                    r.routed_to === "skipped" ? "text-slate-300 mt-0.5" :
                    "text-emerald-600 mt-0.5"
                  }/>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      <span className="text-slate-500">{r.received_at ? new Date(r.received_at).toLocaleString() : ""}</span>
                      <span className="ml-2 font-mono text-[10px] text-slate-400">from {r.from_address || "?"}</span>
                    </div>
                    <div className={`font-bold truncate ${r.routed_to === "skipped" ? "text-slate-400" : "text-slate-800"}`}>{r.subject}</div>
                    {r.wo_number && (
                      <div className="text-[10px] text-cyan-800 font-bold truncate" data-testid={`recent-row-wo-${i}`}>
                        → WO# {r.wo_number}{r.wo_portfolio ? ` · ${r.wo_portfolio}` : ""}{r.wo_address ? ` · ${r.wo_address}` : ""} · {r.wo_status}
                      </div>
                    )}
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex-none">
                    {r.routed_to === "prospects" ? "prospect"
                     : r.routed_to === "existing_wo_update" ? "wo update"
                     : r.routed_to === "skipped" ? "skipped · not a wo"
                     : r.routed_to === "voided" ? "voided"
                     : r.routed_to === "report:open_work_orders" ? "wo report imported"
                     : r.routed_to === "report:moves" ? "moves roster imported"
                     : "new wo"}
                  </span>
                  {(!r.routed_to || r.routed_to === "new_wo") && r.job_id && (
                    <button onClick={(e) => { e.stopPropagation(); voidRow(r); }} data-testid={`void-row-${i}`}
                      title="Not a work order — cancel the WO this created"
                      className="flex-none h-6 px-2 border border-red-300 text-red-700 hover:bg-red-50 text-[9px] font-black uppercase tracking-widest rounded">
                      Void
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}


function StepCard({ n, title, done, children }) {
  return (
    <div className="border-2 border-slate-200 bg-white overflow-hidden">
      <div className={`p-3 flex items-center gap-2 border-b ${done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
        <div className={`w-6 h-6 flex-none inline-flex items-center justify-center font-black text-[11px] ${done ? "bg-emerald-600 text-white" : "bg-slate-900 text-white"}`}>
          {done ? <CheckCircle weight="fill" size={14}/> : n}
        </div>
        <div className="font-black text-slate-900 text-sm uppercase tracking-wide">{title}</div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}


function StatusPill({ live, configured }) {
  const cfg = live
    ? { color: "bg-emerald-600 border-emerald-700 text-white", label: "LIVE" }
    : configured
      ? { color: "bg-amber-500 border-amber-600 text-white", label: "PAUSED" }
      : { color: "bg-slate-300 border-slate-400 text-slate-700", label: "NOT SET" };
  return (
    <div className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 font-black uppercase text-[10px] tracking-widest ${cfg.color}`}
      data-testid="imap-status-pill">
      <Circle weight="fill" size={8} className={live ? "animate-pulse" : ""}/>
      {cfg.label}
    </div>
  );
}
